import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { getAuth } from 'firebase/auth';
import {
  notificationTimeout,
  notificationType,
  scrollHeight,
} from '../common/constants';
import notValid from '../common/notValid';
import pluralise from '../common/pluralise';
import {
  firListDoorStaffCards,
  firAddDoorStaffCard,
  firUpdateDoorStaffCard,
  firDeleteDoorStaffCard,
} from '../firebase';

/**
 * Door Staff Cards — RFID whitelist editor for a single site (rev 119).
 *
 * Replaces the on-disk staff.txt file each Pi used to keep.  Cards
 * written here are picked up by the Pi the next time it polls
 * v2getDoorStaffCards (default every 5 min — see Pi PROJECT_INSTRUCTIONS
 * §11 for the sync cadence and how to verify a freshly-added card
 * actually works at the door).
 *
 * Tab gating: visible to root + superAdmin (set in siteConfig.jsx).
 * The endpoint that the Pi calls is HMAC-signed so MA admins can edit
 * the list without being able to read the shared secret.
 *
 * UX choices:
 *   - cardNo is the doc id, so adding the same card twice updates the
 *     existing row instead of duplicating.
 *   - Card UID input accepts 8-12 digits (RFID readers around the world
 *     emit 8-10 typically; we leave headroom).  Strip leading zeros?
 *     No — readers emit fixed-width strings and Pi compares string
 *     equality, so preserve as typed.
 */
export default function SiteConfigDoorStaffCards({ siteId }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchCards = useCallback(
    async (id) => {
      if (notValid(id)) return;
      setLoading(true);
      try {
        const result = await firListDoorStaffCards(id);
        setCards(result);
      } catch (error) {
        console.error('-- Error fetching door staff cards:', error);
        openMessage('Error fetching door staff cards', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  useEffect(() => {
    fetchCards(siteId);
  }, [siteId, fetchCards]);

  const handleAdd = () => {
    setEditingCard(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingCard(record);
    form.setFieldsValue({ cardNo: record.cardNo, name: record.name });
    setModalOpen(true);
  };

  const handleDelete = async (cardNo) => {
    try {
      await firDeleteDoorStaffCard(siteId, cardNo);
      openMessage('Card removed', timeoutSuccess, typeSuccess);
      fetchCards(siteId);
    } catch (error) {
      console.error('-- Error deleting card:', error);
      openMessage('Error removing card', timeoutError, typeError);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const trimmedCard = String(values.cardNo).trim();
      const trimmedName = String(values.name).trim();
      const uid = getAuth().currentUser?.uid || '';

      if (editingCard) {
        // Edit only mutates the display name. Changing the card UID
        // requires delete + add (different doc id).
        await firUpdateDoorStaffCard(siteId, editingCard.cardNo, { name: trimmedName });
        openMessage('Card updated', timeoutSuccess, typeSuccess);
      } else {
        await firAddDoorStaffCard(siteId, trimmedCard, trimmedName, uid);
        openMessage('Card added', timeoutSuccess, typeSuccess);
      }
      setModalOpen(false);
      fetchCards(siteId);
    } catch (error) {
      if (error?.errorFields) return; // validation error, message already shown inline
      console.error('-- Error saving card:', error);
      openMessage('Error saving card', timeoutError, typeError);
    }
  };

  const columns = [
    {
      title: 'Card UID',
      dataIndex: 'cardNo',
      key: 'cardNo',
      width: 200,
      sorter: (a, b) => a.cardNo.localeCompare(b.cardNo),
      render: (v) => <code>{v}</code>,
    },
    {
      title: 'Display Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Popconfirm
            title="Remove this card?"
            description="The Pi will lose access at the next sync (≤5 min)."
            onConfirm={() => handleDelete(record.cardNo)}
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button icon={<DeleteOutlined />} danger>Remove</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <span>
          <IdcardOutlined style={{ marginRight: 8 }} />
          {cards.length} {pluralise(cards.length, 'card')} authorized to swipe in via RFID at any door on this site.
          {' '}Pi controllers poll every ~5 min — additions/removals propagate on that cadence.
        </span>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Card
        </Button>
      </Flex>

      <Table
        columns={columns}
        dataSource={cards}
        rowKey="cardNo"
        loading={loading}
        pagination={{ pageSize: 50 }}
        scroll={{ y: scrollHeight }}
      />

      <Modal
        title={editingCard ? 'Edit Card' : 'Add Card'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="Save"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Card UID"
            name="cardNo"
            extra="The 10-digit number the RFID reader emits on swipe. To capture a new card, ssh into the Pi and run the script in --capture mode (see PROJECT_INSTRUCTIONS §11.4)."
            rules={[
              { required: true, message: 'Card UID required' },
              { pattern: /^\d{8,12}$/, message: 'Card UID should be 8–12 digits' },
            ]}
          >
            <Input
              disabled={!!editingCard}
              placeholder="e.g. 0483996050"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label="Display Name"
            name="name"
            rules={[
              { required: true, message: 'Display name required' },
              { min: 1, max: 64, message: 'Name should be 1–64 characters' },
            ]}
            extra="Shown in the Pi's console log on swipe — useful for audit logs."
          >
            <Input placeholder="e.g. Samuel" autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

SiteConfigDoorStaffCards.propTypes = {
  siteId: PropTypes.string.isRequired,
};
